// "Where won deals came from" — per-source CRM table with observed win
// rates and annualized won revenue (computeSourceTable). All divisions,
// all time; the dashboard's sourced-won tile stays the BWM-year cut.

import { useMemo } from 'react';
import { usePipeline } from '../../../context/PipelineContext';
import { computeSourceTable, SOURCE_LABELS } from '../attributionMath';
import { Card, Eyebrow, Dot } from '../ui';
import { formatCurrency } from '../../../utils/formatting';

const DOT_COLORS = ['#006D78', '#022889', '#2596BE', '#3EAAD0', '#FF6E83', '#B7791F'];

export default function SourceTableCard() {
  const { prospects } = usePipeline();
  const rows = useMemo(() => computeSourceTable(prospects), [prospects]);

  return (
    <Card lash="blue" className="p-4">
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>Where won deals came from · live from CRM</Eyebrow>
        <span className="text-ink-3 text-[12px]">observed win rates · all time</span>
      </div>

      {rows.length === 0 ? (
        <div className="text-[12px] text-ink-2">No prospects in the pipeline yet.</div>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Source</Th>
              <Th right>Prospects</Th>
              <Th right>Won</Th>
              <Th right>Win rate</Th>
              <Th right>Won revenue</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.source}>
                <Td className="font-semibold text-ink">
                  <Dot color={DOT_COLORS[i % DOT_COLORS.length]} className="mr-2" />
                  {SOURCE_LABELS[r.source] || r.source}
                </Td>
                <Td right className="tabular-nums">{r.prospects}</Td>
                <Td right className="tabular-nums">{r.won}</Td>
                <Td right className="tabular-nums">
                  {r.winRate == null ? <span className="text-ink-3">—</span> : `${Math.round(r.winRate * 100)}%`}
                </Td>
                <Td right className="tabular-nums font-bold text-navy">
                  {r.revenue > 0 ? formatCurrency(r.revenue) : <span className="text-ink-3 font-normal">—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="text-[11px] text-ink-3 mt-2.5">
        Win rate = won over decided outcomes (won + lost). Revenue is annualized first-year value of won deals.
      </div>
    </Card>
  );
}

function Th({ children, right }) {
  return (
    <th className={`text-[10.5px] tracking-[0.06em] uppercase text-ink-3 font-black px-2.5 py-2 border-b border-line ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Td({ children, right, className = '' }) {
  return (
    <td className={`px-2.5 py-2 border-b border-line-2 text-[13px] ${right ? 'text-right' : 'text-left'} ${className}`}>
      {children}
    </td>
  );
}
